import React, { useEffect, useState } from 'react';
import { ParticipantView } from '@views';
import {
  Alert,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { palette } from '@app/palette';
import Avatar from '@mui/material/Avatar';
import { CohortUserGoalEditor } from './CohortUserGoalEditor';
import { AddActivityDialog } from '../Admin/AddActivityDialog';
import { CohortUserJailCardEditor } from './CohortUserJailCardEditor';
import { CohortUserPaymentEditor } from './CohortUserPaymentEditor';
import { CohortUserStore, GroupStore, handleApiResponse } from '@stores';
import logger from '@utils/logger';
import { ChatStore } from '@app/api/ChatStore';
import { SendCustomGiftCardAction } from '@actions/Admin/SendCustomGiftCardAction';

interface ParticipantListProps {
  length: number;
  participants: ParticipantView[];
  editMode: boolean;
}

export const ParticipantList: React.FC<ParticipantListProps> = ({ length, participants, editMode }) => {
  const cohortUserStore = new CohortUserStore();
  const sendGiftCardAction = new SendCustomGiftCardAction();
  const [openGoalEditorModal, setOpenGoalEditorModal] = useState(false);
  const [openActivityModal, setOpenActivityModal] = useState(false);
  const [openJailCardModal, setOpenJailCardModal] = useState(false);
  const [openGiftCardModal, setOpenGiftCardModal] = useState(false);
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [currentParticipants, setCurrentParticipants] = useState<ParticipantView[]>([]);
  const [selectedParticipant, setSelectedParticipant] = useState<ParticipantView | null>(null);
  const [selectedCohortId, setSelectedCohortId] = useState<number | null>(null);
  const [showAlert, setShowAlert] = useState(false);
  const [showError, setShowError] = useState(false);
  const [giftCardAmount, setGiftCardAmount] = useState<string>('');
  const [giftCardError, setGiftCardError] = useState<string>('');
  const [sendingGiftCard, setSendingGiftCard] = useState(false);

  useEffect(() => {
    setCurrentParticipants(participants);
  }, [participants]);

  const handleManageGoalsClick = (participant: ParticipantView) => {
    setSelectedParticipant(participant);
    setOpenGoalEditorModal(true);
  };

  const handleManageJailCardsClick = (participant: ParticipantView) => {
    setSelectedParticipant(participant);
    setOpenJailCardModal(true);
  };

  const handleManageActivityClick = (participant: ParticipantView) => {
    setSelectedParticipant(participant);
    setOpenActivityModal(true);
  };

  const handleSendGiftCardClick = (participant: ParticipantView) => {
    setSelectedParticipant(participant);
    setGiftCardAmount('');
    setGiftCardError('');
    setOpenGiftCardModal(true);
  };

  const handleManagePaymentClick = async (participant: ParticipantView) => {
    const response = await cohortUserStore.readById(participant.CohortUser.id);
    setSelectedCohortId(response.result.cohortId);
    setSelectedParticipant(participant);
    setOpenPaymentModal(true);
  };

  const refreshParticipantData = async (participantEmail: string) => {
    try {
      // Find the participant in the current list
      const participantToUpdate = currentParticipants.find((p) => p.email === participantEmail);
      if (!participantToUpdate) return;

      // Fetch updated cohort user data
      const response = await cohortUserStore.readById(participantToUpdate.CohortUser.id);
      if (response.status === 'SUCCESS') {
        // Update the participant in the current list with fresh data
        setCurrentParticipants((prev) =>
          prev.map((p) => (p.email === participantEmail ? { ...p, CohortUser: response.result } : p))
        );
      }
    } catch (error) {
      logger.error('Failed to refresh participant data:', { error });
    }
  };
  // Handler to close the modal.
  const handleCloseModal = () => {
    setOpenGoalEditorModal(false);
    setOpenJailCardModal(false);
    setOpenActivityModal(false);
    setOpenGiftCardModal(false);
    setOpenPaymentModal(false);
    setSelectedParticipant(null);
    setGiftCardAmount('');
    setGiftCardError('');
    setSelectedCohortId(null);
  };

  const handleParticipantActivation = async (participant: ParticipantView) => {
    setSelectedParticipant(participant);
    const cohortId = (await cohortUserStore.readById(participant.CohortUser.id)).result.cohortId;
    const groupStore = new GroupStore();
    const cohort = await groupStore.ReadOne(cohortId);
    const groupData = cohort.result;
    const cohortUser = participant.CohortUser;

    // Flip isActive in memory
    cohortUser.isActive = !cohortUser.isActive;

    try {
      // Persist the change to the server
      await handleApiResponse(async () => await cohortUserStore.update(cohortUser, cohortId));

      // Update the current participant's isActive status in state
      setCurrentParticipants((prev) =>
        prev.map((p) => (p.email === participant.email ? { ...p, CohortUser: cohortUser } : p))
      );

      // If the participant is now active, add them to the cohort chat
      // Otherwise, remove them from the chat
      const chatStore = new ChatStore();
      if (cohortUser.isActive) {
        await chatStore.InviteUsersToChat(groupData.channel, [cohortUser.userId.toString()]);
      } else {
        await chatStore.RemoveUsersFromChat(groupData.channel, [cohortUser.userId.toString()]);
      }

      // Show success alert
      setShowAlert(true);
      setTimeout(() => {
        setShowAlert(false);
      }, 3000);
    } catch (error) {
      // Show error alert and log the error
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 3000);
      logger.error('Failed to update cohort user:', error || undefined);
    }
  };

  const handleSendGiftCard = async () => {
    if (!selectedParticipant) return;

    const amount = parseFloat(giftCardAmount);
    if (isNaN(amount) || amount < 5 || amount > 200 || !Number.isInteger(amount)) {
      setGiftCardError('Please enter a whole number between 5 and 200');
      return;
    }

    setSendingGiftCard(true);
    setGiftCardError('');

    try {
      const response = await sendGiftCardAction.Execute(selectedParticipant.CohortUser.id, amount);

      if (response.status === 'SUCCESS') {
        setShowAlert(true);
        setTimeout(() => {
          setShowAlert(false);
        }, 3000);
        handleCloseModal();
      } else if (response.status === 'VALIDATION_ERROR') {
        setGiftCardError(response.statusMessage);
      } else {
        setGiftCardError('Failed to send gift card. Please try again.');
      }
    } catch (error) {
      setGiftCardError('Failed to send gift card. Please try again.');
      logger.error('Failed to send gift card:', error || undefined);
    } finally {
      setSendingGiftCard(false);
    }
  };

  return (
    <>
      <Box borderRadius={8} border={1} borderColor={palette.background.border} display={'block'} position={'relative'}>
        <Typography
          variant={'subtitle1'}
          sx={{
            backgroundColor: palette.background.paper,
            position: 'absolute',
            top: -32,
            left: 48,
            padding: 2,
          }}
        >
          Participants ({length})
        </Typography>
        <List>
          {currentParticipants.map((p) => {
            return (
              <ListItem key={p.email}>
                <ListItemAvatar>
                  <Avatar sx={{ backgroundColor: palette.primary.main }}>{p.email[0]}</Avatar>
                </ListItemAvatar>
                <ListItemText>
                  {p.name ? `${p.name} (${p.email})` : p.email}
                  {p.email === selectedParticipant?.email && (
                    <>
                      <Collapse in={showAlert} sx={{ maxWidth: '50%' }}>
                        <Alert severity={'success'} variant={'filled'} sx={{ fontFamily: 'roboto', width: 'auto' }}>
                          Cohort user updated successfully
                        </Alert>
                      </Collapse>
                      <Collapse in={showError} sx={{ maxWidth: '50%' }}>
                        <Alert severity={'error'} variant={'filled'} sx={{ fontFamily: 'roboto' }}>
                          Failed to update cohort user
                        </Alert>
                      </Collapse>
                    </>
                  )}
                  <Typography component="span" sx={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                    Cohort User ID: {p.CohortUser.id}
                  </Typography>
                  {/* Show the user's payment status */}
                  <Typography
                    component="span"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: p.CohortUser.pledgeReceived ? 'green' : 'red',
                      fontSize: '14px',
                    }}
                  >
                    Payment Status: {p.CohortUser.pledgeReceived ? 'Paid' : 'Unpaid'}
                  </Typography>
                  {editMode && (
                    <div>
                      <br />
                      <Grid container gap={1}>
                        <Button variant={'outlined'} onClick={() => handleManageGoalsClick(p)}>
                          Manage goals
                        </Button>
                        <Button variant={'outlined'} onClick={() => handleManageActivityClick(p)}>
                          Manage Activity
                        </Button>
                        <Button variant={'outlined'} onClick={() => handleManageJailCardsClick(p)}>
                          Manage Jail Cards
                        </Button>
                        <Button variant={'outlined'} onClick={() => handleParticipantActivation(p)}>
                          {p.CohortUser.isActive ? 'Deactivate Participant' : 'Activate Participant'}
                        </Button>
                        <Button variant={'outlined'} onClick={() => handleSendGiftCardClick(p)}>
                          Send Custom Gift Card
                        </Button>
                        <Button variant={'outlined'} onClick={() => handleManagePaymentClick(p)}>
                          Manage Payment
                        </Button>
                      </Grid>
                    </div>
                  )}
                </ListItemText>
                <ListItemSecondaryAction>{p.role}</ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* Add activity modal */}
      {selectedParticipant && (
        <AddActivityDialog
          open={openActivityModal}
          onClose={handleCloseModal}
          participant={selectedParticipant}
        ></AddActivityDialog>
      )}

      {/* Goals modal */}
      <Dialog
        open={openGoalEditorModal}
        onClose={handleCloseModal}
        maxWidth="xl"
        fullWidth={true}
        slotProps={{
          backdrop: { style: { backgroundColor: 'background.paper' } },
        }}
      >
        {selectedParticipant && <CohortUserGoalEditor cohortUser={selectedParticipant} />}
      </Dialog>

      {/* Jail cards modal */}
      <Dialog
        open={openJailCardModal}
        onClose={handleCloseModal}
        maxWidth="xl"
        fullWidth={true}
        slotProps={{
          backdrop: { style: { backgroundColor: 'background.paper' } },
        }}
      >
        {selectedParticipant && <CohortUserJailCardEditor cohortUser={selectedParticipant} />}
      </Dialog>

      {/* Gift card modal */}
      <Dialog open={openGiftCardModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>Send Custom Gift Card</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, fontFamily: 'Roboto' }}>
            Send a custom gift card to {selectedParticipant?.email}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Amount ($)"
            type="number"
            fullWidth
            variant="outlined"
            value={giftCardAmount}
            onChange={(e) => setGiftCardAmount(e.target.value)}
            error={!!giftCardError}
            helperText={giftCardError || 'Enter a whole number between 5 and 200'}
            slotProps={{
              htmlInput: {
                min: 5,
                max: 200,
                step: 1,
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button onClick={handleSendGiftCard} variant="contained" disabled={sendingGiftCard || !giftCardAmount}>
            {sendingGiftCard ? 'Sending...' : 'Send Gift Card'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment modal */}
      <Dialog
        open={openPaymentModal}
        onClose={handleCloseModal}
        maxWidth="xl"
        fullWidth={true}
        slotProps={{
          backdrop: { style: { backgroundColor: 'background.paper' } },
        }}
      >
        {selectedParticipant && selectedCohortId && (
          <CohortUserPaymentEditor
            cohortUser={selectedParticipant}
            cohortId={selectedCohortId}
            onSave={refreshParticipantData}
          />
        )}
      </Dialog>
    </>
  );
};
